// src/modules/assetManagement/components/MultiSelectFilter.tsx
// Filter dropdown with a search box and checkboxes; the button summarises what is picked.
import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

export interface FilterOption {
  value: string;
  label: string;
  meta?: string;
}

const MultiSelectFilter: React.FC<{
  /** Plural noun for the button, e.g. "events" */
  noun: string;
  icon?: React.ReactNode;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  className?: string;
  /** Shown above the list, e.g. why the options are narrowed */
  note?: string;
}> = ({ noun, icon, options, selected, onChange, className = "w-48", note }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const shown = options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const firstLabel = options.find((o) => o.value === selected[0])?.label ?? selected[0];
  const summary =
    selected.length === 0 ? `Any ${noun.replace(/s$/, "")}` : selected.length === 1 ? firstLabel : `${selected.length} ${noun}`;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 pl-3 pr-2.5 py-2 border rounded-lg text-sm bg-white text-left transition ${
          selected.length ? "border-slate-500 text-slate-900 font-medium" : "border-slate-300 text-slate-700"
        } ${open ? "ring-2 ring-slate-200" : ""}`}
      >
        {icon && <span className="text-slate-400 shrink-0">{icon}</span>}
        <span className="truncate flex-1">{summary}</span>
        {selected.length > 1 && (
          <span className="shrink-0 px-1.5 rounded-full bg-slate-900 text-white text-[10px] font-semibold">{selected.length}</span>
        )}
        <ChevronDown size={14} className={`shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-1.5 left-0 w-72 max-w-[85vw] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${noun}`}
                className="w-full pl-8 pr-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </div>
            {note && <p className="text-[11px] text-slate-500 mt-1.5 px-0.5">{note}</p>}
          </div>
          <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1">
            {shown.length === 0 && <li className="px-3 py-3 text-sm text-slate-500 text-center">No {noun} found</li>}
            {shown.map((o) => {
              const on = selected.includes(o.value);
              return (
                <li key={o.value} role="option" aria-selected={on}>
                  <label className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" className="h-4 w-4 accent-slate-700 shrink-0" checked={on} onChange={() => toggle(o.value)} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-800">{o.label}</span>
                      {o.meta && <span className="block truncate text-xs text-slate-500">{o.meta}</span>}
                    </span>
                    {on && <Check size={14} className="text-slate-700 shrink-0" />}
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs">
            <button
              type="button"
              className="text-slate-600 hover:text-slate-900 disabled:opacity-40"
              disabled={shown.length === 0}
              onClick={() => onChange([...new Set([...selected, ...shown.map((o) => o.value)])])}
            >
              Select all{query ? " shown" : ""}
            </button>
            <button
              type="button"
              className="text-slate-600 hover:text-slate-900 disabled:opacity-40"
              disabled={selected.length === 0}
              onClick={() => onChange([])}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;